import { Box, Button, Stack, Typography } from '@mui/material';
import MuiButton from '@mui/material/Button';
import classNames from 'classnames';
import { autobind } from 'core-decorators';
import { isEqual } from 'lodash';
import debounce from 'lodash.debounce';
import PropTypes from 'prop-types';
import { Component, Fragment, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Label,
    Line,
    LineChart,
    ReferenceLine,
    Tooltip as TooltipGraphics,
    XAxis,
    YAxis,
} from 'recharts';
import { bindActionCreators } from 'redux';
import FooterCarbonCalculator from '/@/components/carbon-calculator/footer';
import HeaderCarbonCalculator from '/@/components/carbon-calculator/header';
import Container from '/@/components/container';
import Message from '/@/components/message';
import Snackbar from '/@/components/snackbar';
import Spinner from '/@/components/spinner';
import settings from '/@/core/settings';
import { useFieldById, useMyFields } from '/@/hooks/fields';
import { withRouter } from '/@/hooks/with-router';
import { _ } from '/@/locale';
import {
    setAllCrops,
    setAllFarmingTypes,
    setAllSoilTypes,
    setCalculationResult,
    setCampaignFrom,
    setCampaigns,
    setCampaignTo,
    setClay,
    setDensity,
    setFarmingType,
    setOrganicMaterial,
    setSelectedCampaign,
    setSelectedCampaigns,
    setSoiltype,
} from '/@/store/carbon-calculator/actions';
import {
    getAllSoilTypesOptions,
    getCalculationResult,
    getCampaignFrom,
    getCampaignsOptions,
    getCampaignTo,
    getClay,
    getCrops,
    getDensity,
    getFarmingOptions,
    getOrganicMaterial,
    getRequiredFieldsComplete,
    getSelectedCampaigns,
    getSelectedFarmingOption,
    getSoilType,
} from '/@/store/carbon-calculator/selectors';
import { fetchHelper } from '/@/store/session/actions';
import { getSessionCountry } from '/@/store/session/selectors';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import { isMobile, isPhone } from '/@/utils';
import { reverse } from '/@/utils/router';
import Input from '/@/v3/components/forms/input';
import Select from '/@/v3/components/forms/select';
import Title from '/@/v3/components/title';
import Tooltip from '/@/v3/components/tooltip';
import NewFieldGrowersModal from '../dashboard/growers/components/new-field-modal';
import styles from './styles.module.css';

@withRouter
@connect(
    (state, props) => {
        return {
            campaignFrom: getCampaignFrom(state),
            campaignTo: getCampaignTo(state),
            selectedCampaigns: getSelectedCampaigns(state),
            farmingOptions: getFarmingOptions(state),
            farmingType: getSelectedFarmingOption(state),
            allSoilTypes: getAllSoilTypesOptions(state),
            soilType: getSoilType(state),
            crops: getCrops(state),
            clay: getClay(state),
            organicMaterial: getOrganicMaterial(state),
            density: getDensity(state),
            requiredFieldsComplete: getRequiredFieldsComplete(state),
            calculationResult: getCalculationResult(state),
            campaignsOptions: getCampaignsOptions(),
        };
    },
    (dispatch) =>
        bindActionCreators(
            {
                setAllFarmingTypes,
                setAllSoilTypes,
                setAllCrops,
                setCampaigns,
                setSelectedCampaigns,
                setSelectedCampaign,
                setFarmingType,
                setCampaignFrom,
                setCampaignTo,
                setSoiltype,
                setClay,
                setOrganicMaterial,
                setDensity,
                setCalculationResult,
            },
            dispatch
        )
)
@autobind
class CarbonCalculator extends Component {
    static contextTypes = {
        history: PropTypes.object,
        units: PropTypes.object,
    };

    state = {
        showCornReco: false,
        showCampaigns: false,
        selectedSeason: 0,
        showGraphics: false,
        crop: '',
        zoneId: '',
        graphicData: '',
        hideDropdown: false,
        temperature: 16.8,
        collapsed: false,
        errorMessage: false,
        showSnackbar: false,
        campaignToError: false,
        campaignFromError: false,
        otherSoilType: false,
        oldCampaignFrom: undefined,
        oldCampaignTo: undefined,
    };

    componentDidMount() {
        this.fetchFarmingTypes();
        this.fetchSoilTypes(this.props.fieldZoneIds);
        this.props.setCampaigns(this.props.campaignsOptions);
        this.fetchCoefficientsCrops();
    }

    componentDidUpdate(prevProps, prevState) {
        if (
            this.props.requiredFieldsComplete &&
            (prevProps.campaignFrom !== this.props.campaignFrom ||
                prevProps.campaignTo !== this.props.campaignTo ||
                prevProps.farmingType !== this.props.farmingType ||
                prevProps.soilType !== this.props.soilType ||
                prevState.temperature !== this.state.temperature ||
                prevProps.clay !== this.props.clay ||
                prevProps.density !== this.props.density ||
                prevProps.organicMaterial !== this.props.organicMaterial) &&
            this.validateSelectedCampaigns()
        ) {
            this.debounceFetchCarbonResults();
        }

        if (
            prevProps.calculationResult.length !== this.props.calculationResult.length &&
            this.props.selectedCampaigns.yield !== 0
        ) {
            setTimeout(() => this.setState({ showSnackbar: false }), 3000);
        }

        if (
            !isEqual(prevProps.calculationResult, this.props.calculationResult) &&
            this.props.calculationResult.length > 0
        )
            mpTrackEvent('Sustainable Rotations Simulations Generated', undefined);

        if (prevProps.calculationResult !== this.props.calculationResult && this.props.selectedCampaigns.yield !== 0) {
            setTimeout(() => this.setState({ showSnackbar: false }), 3000);
        }

        if (this.state.errorMessage) {
            setTimeout(() => this.setState({ errorMessage: false }), 5000);
        }

        if (prevProps.fieldZoneIds !== this.props.fieldZoneIds) {
            this.fetchSoilTypes(this.props.fieldZoneIds);
        }
    }

    handleZoneSelection(event) {
        const fields = this.props.fields;
        const field = fields.find((field) => field.id === event);
        this.props.setSelectedFieldId(event);
        this.setState({
            otherSoilType: false,
            showCampaigns: false,
            showGraphics: false,
        });
        this.props.setCalculationResult([]);
        this.props.setSoiltype(undefined);
    }

    updateValueForMessage(value) {
        this.setState({ errorMessage: value });
    }

    componentWillUnmount() {
        this.cleanFields();
    }

    getRequestBody() {
        const { selectedCampaigns } = this.props;
        const campaigns = Object.keys(selectedCampaigns)
            .map((key) => {
                return {
                    season: key,
                    crops: selectedCampaigns[key]['crops'].filter((crop) => Boolean(crop.crop) && Boolean(crop.yield)), // Solo manda los crops con data completa
                };
            })
            .filter((campaign) => campaign['crops'].length > 0);

        return { campaigns };
    }

    validateSelectedCampaigns() {
        // Valida que exista al menos un crop con data valida cargado en alguna de las campañas
        const { selectedCampaigns } = this.props;

        return Object.keys(selectedCampaigns).some((season) => {
            if (selectedCampaigns[season]['crops'].length > 0) {
                return selectedCampaigns[season]['crops'].some((crop) => !!crop.crop && !!crop.yield);
            }
            return false;
        });
    }

    async fetchCarbonResults() {
        const { farmingType, clay, organicMaterial, density } = this.props;
        const body = this.getRequestBody();
        const qparams = new URLSearchParams(this.context.history.location.search);
        const id = qparams.get('id');

        const paramsObj = {
            farming: farmingType,
            soil_clay: clay,
            temperature: this.state.temperature,
            organic_material: organicMaterial,
            apparent_density: density,
            variable: 'y',
        };
        if (id) paramsObj['id'] = id;
        const queryParams = new URLSearchParams(paramsObj);
        const url = settings.urls.carbonCalculator.calculateAr + '?' + queryParams.toString();

        const data = await fetchHelper(url, {
            method: 'post',
            body: JSON.stringify(body),
        });

        const seasons = data.data.response.seasons.map((season) => {
            return {
                ...season,
                co: {
                    simulated: season.co.simulated / this.context.units.toArea(1, 4).value,
                    fixed: season.co.fixed / this.context.units.toArea(1, 4).value,
                },
            };
        });

        this.props.setCalculationResult(seasons);

        // Set ID to update DB results
        if (!id) {
            const newId = data.data.response.id;
            if (newId) this.context.history({ search: `id=${newId}` }, { replace: true });
        }
    }

    debounceFetchCarbonResults = debounce(this.fetchCarbonResults, 500);

    async fetchFarmingTypes() {
        const url = `${settings.urls.carbonCalculator.farmingTypes}`;
        const data = await fetchHelper(url, {
            method: 'get',
        });
        this.props.setAllFarmingTypes(data.data.response);
    }

    async fetchSoilTypes(zoneIds) {
        const url = `${settings.urls.carbonCalculator.soilTypes}?zone_ids=${zoneIds}`;
        const data = await fetchHelper(url, {
            method: 'get',
        });
        this.props.setAllSoilTypes(data.data.response);
    }

    async fetchCoefficientsCrops() {
        const url = `${settings.urls.carbonCalculator.cropsCoefficients}`;
        const data = await fetchHelper(url, {
            method: 'get',
        });
        this.props.setAllCrops(data.data.response);
    }

    setSoilTypeValue(val, id) {
        const soil = this.props.allSoilTypes?.find((soil) => soil.label === val);
        this.props.setSoiltype(soil);
        this.props.setOrganicMaterial(soil.organicMaterial);
        this.props.setDensity(soil.apparentDensity);
        this.props.setClay(soil.clay);
        this.updateValueForMessage(soil.label === 'Otros' || soil.label === 'Outros' || soil.label === 'Other');
        if (val === 'Otros' && this.props.farmingType !== undefined) {
            this.setState({ otherSoilType: true });
        }
        if (val !== 'Otros') {
            this.setState({ otherSoilType: false });
        }
    }

    setClayValue(value) {
        const clay = value;
        this.props.setClay(clay);
        if (value > 0) {
            this.setState({ errorMessage: false });
        }
    }

    setDensityValue(value) {
        const density = value;
        this.props.setDensity(density);
        if (value > 0) {
            this.setState({ errorMessage: false });
        }
    }

    setOrganicMaterialValue(value) {
        const organicMaterial = value;
        this.props.setOrganicMaterial(organicMaterial);
        if (value > 0) {
            this.setState({ errorMessage: false });
        }
    }

    round(value, digits) {
        const factor = 10 ** digits;
        value += Math.sign(value) * Number.EPSILON;
        return Math.round(value * factor) / factor;
    }

    cleanFields() {
        this.props.setCampaignFrom(undefined);
        this.props.setCampaignTo(undefined);
        this.props.setSoiltype(undefined);
        this.props.setOrganicMaterial(undefined);
        this.props.setDensity(undefined);
        this.props.setClay(undefined);
        this.props.setFarmingType(undefined);
        this.setState({
            showCornReco: false,
            showCampaigns: false,
            selectedSeason: 0,
            showGraphics: false,
        });
        this.props.setCalculationResult([]);
        this.props.setSelectedCampaigns({});
    }

    handleCornSelection() {
        this.setState({ showCornReco: true });
    }

    getMaxAndMinValues(values) {
        const res = values.map((res) => {
            return this.round(res.co.simulated, 2);
        });
        return res;
    }

    getMaxAndMinSecondValues(values) {
        const res = values.map((res) => {
            if (res.co.fixed !== 0) {
                return this.round(res.co.fixed, 2);
            }
        });
        return res;
    }

    debounceSnackbar = debounce(this.handleSnackbar, 600);

    handleSnackbar() {
        this.setState({ showSnackbar: true });
    }

    filterOptions(index, season) {
        const campaign = this.props.selectedCampaigns[season];
        const newCampaign = { season: campaign.season, crops: [...campaign.crops] };
        return newCampaign.crops?.[index]?.options.filter((option) => newCampaign.crops[index].key !== option.value);
    }

    cleanSelectFields(index, season) {
        const campaign = this.props.selectedCampaigns[season];
        const newCampaign = { season: campaign.season, crops: [...campaign.crops] };
        newCampaign.crops[index].yield = '';
        newCampaign.crops[index].key = '';
        newCampaign.crops[index].crop = '';
    }

    handleCropChange(field, index, season, value) {
        const campaign = this.props.selectedCampaigns[season];

        const newCampaign = { season: campaign.season, crops: [...campaign.crops] };

        if (field == 'yield') {
            newCampaign['crops'][index][field] = value;
            if (value !== '') this.debounceSnackbar();
        } else if (field == 'crop') {
            newCampaign['crops'][index]['crop'] = value.label;
            newCampaign['crops'][index]['key'] = value.value;

            if (this.props.selectedCampaigns[season]['crops'].length === 2) {
                newCampaign.crops[this.props.selectedCampaigns[season].crops.length - 1].options = this.filterOptions(
                    0,
                    season
                );

                if (index === 0 && value.value === newCampaign.crops?.[1]?.key) {
                    this.cleanSelectFields(1, season);
                }
            }

            if (this.props.selectedCampaigns[season]['crops'].length === 3) {
                const secondOptions = this.filterOptions(0, season);
                const thirdOptions = secondOptions.filter((option) => newCampaign.crops?.[1]?.key !== option.value);

                newCampaign.crops[this.props.selectedCampaigns[season].crops.length - 2].options = secondOptions;
                newCampaign.crops[this.props.selectedCampaigns[season].crops.length - 1].options = thirdOptions;

                if (index === 0 && value.value === newCampaign.crops?.[1]?.key) {
                    this.cleanSelectFields(1, season);
                }

                if (index === 0 && value.value === newCampaign.crops?.[2]?.key) {
                    this.cleanSelectFields(2, season);
                }

                if (index === 1 && value.value === newCampaign.crops?.[2]?.key) {
                    this.cleanSelectFields(2, season);
                }
            }
            this.setState({ crop: value.value === 3 ? 'corn' : '' });
        }

        this.props.setSelectedCampaign({ season, newCampaign });

        if (this.validateSelectedCampaigns()) this.debounceFetchCarbonResults();
    }

    addCropToCampaign(season) {
        const data =
            this.props.selectedCampaigns?.[season]?.crops[this.props.selectedCampaigns?.[season]?.crops.length - 1];

        if (this.props.selectedCampaigns[season]['crops'].length >= 3) return;

        const options = data.options.filter((option) => data.key !== option.value);

        const newCrop = { crop: '', yield: '', options: options };

        const newCampaign = { ...this.props.selectedCampaigns[season] };
        newCampaign['crops'] = [...newCampaign['crops'], newCrop];

        this.props.setSelectedCampaign({ season, newCampaign });

        if (this.validateSelectedCampaigns()) this.debounceFetchCarbonResults();
    }

    removeCropFromCampaign(season, index) {
        const newCampaign = { ...this.props.selectedCampaigns[season] };

        newCampaign['crops'] = [...newCampaign['crops'].filter((el, i) => i !== index)];

        if (index === 0) {
            newCampaign.crops[0].options = this.props.crops;
        } else if (index === 1 && this.props.selectedCampaigns[season]['crops'].length === 3) {
            newCampaign.crops[newCampaign.crops.length - 1].options = this.filterOptions(0, season);
        }

        this.props.setSelectedCampaign({ season, newCampaign });

        if (this.validateSelectedCampaigns()) this.debounceFetchCarbonResults();
    }

    createOrUpdateSelectedCampaigns({ campaignFrom = undefined, campaignTo = undefined }) {
        const { selectedCampaigns } = this.props;

        // Tuve que hacer esto porque el store no sincronizaba
        if (!campaignTo) {
            campaignTo = this.props.campaignTo;
        }
        if (!campaignFrom) {
            campaignFrom = this.props.campaignFrom;
        }
        if (!campaignFrom || !campaignTo) return;

        const campaigns = {};
        for (let i = campaignFrom; i <= campaignTo; i++) {
            if (selectedCampaigns[i] === undefined) {
                // La crea si no existe
                campaigns[i] = {
                    season: i,
                    crops: [{ crop: '', yield: '', options: this.props.crops }],
                };
            } else {
                campaigns[i] = selectedCampaigns[i];
            }
        }

        this.props.setSelectedCampaigns(campaigns);
    }

    campaignFromChanged(val) {
        if (this.props.calculationResult) {
            this.setState({ oldCampaignFrom: this.props.campaignFrom });
        }

        if (!this.state.oldCampaignFrom) {
            this.setState({ oldCampaignFrom: val });
        }

        if (this.state.campaignFromError) {
            this.setState({ campaignFromError: false });
        }
        this.props.setCampaignFrom(val);
        this.createOrUpdateSelectedCampaigns({ campaignFrom: val, campaignTo: undefined });
        this.setState({ selectedSeason: 0 });
        if (this.props.campaignTo <= val) {
            this.props.setCampaignTo(undefined);
            this.props.setCalculationResult([]);
            this.setState({ showGraphics: false, showCampaigns: false, selectedSeason: 0 });
            this.props.setSelectedCampaigns([]);
        }

        if (this.state.oldCampaignTo < this.props.campaignTo && this.state.oldCampaignFrom < val) {
            this.props.setCalculationResult([]);
        }
    }

    campaignToChanged(val) {
        if (this.props.calculationResult) {
            this.setState({ oldCampaignTo: this.props.campaignTo });
        }

        if (this.state.oldCampaignFrom > this.props.campaignFrom && this.props.campaignTo > val) {
            this.props.setCalculationResult([]);
        }
        if (this.state.campaignToError) {
            this.setState({ campaignToError: false });
        }
        this.props.setCampaignTo(val);
        this.createOrUpdateSelectedCampaigns({ campaignFrom: undefined, campaignTo: val });
    }

    setSelectedSeason(index) {
        this.setState({ selectedSeason: index });
    }

    handleTempChange(value) {
        this.setState({ temperature: value });
    }

    handleShowGraphics(id) {
        if (this.state.showGraphics === false && this.props.calculationResult.length > 0) {
            this.setState({ showGraphics: true });
        }
        this.debounceScroll(id);
    }

    handleScroll(id) {
        const element = document.getElementById(id);
        element.scrollIntoView({ block: 'end', behavior: 'smooth' });
    }

    debounceScroll = debounce((id) => this.handleScroll(id), 50);

    handleLoadMore(id) {
        if (
            this.state.temperature > 0 &&
            this.props.clay > 0 &&
            this.props.density > 0 &&
            this.props.organicMaterial > 0 &&
            this.props.campaignFrom !== undefined &&
            this.props.campaignTo !== undefined &&
            this.props.farmingType !== undefined
        ) {
            this.setState({ showCampaigns: true });
            if (isMobile() && !this.state.collapsed) {
                this.handleCollapseClick();
            }
        }

        if (this.props.campaignTo === undefined) {
            this.setState({ campaignToError: true });
        }

        if (this.props.campaignFrom === undefined) {
            this.setState({ campaignFromError: true });
        }

        this.debounceScroll(id);
    }

    handleCollapseClick() {
        this.setState((state) => ({
            collapsed: !state.collapsed,
        }));
    }

    handleOnKeyPress(e) {
        if (e.keyCode === 13) {
            this.handleLoadMore('scroll');
        }
    }

    renderErrorMessage() {
        return (
            <Message type='warning' appearance='fixed' showIcon color='red' responsive fixed>
                {_('Eligiendo "Otros" en tipo de suelo, deberán cargarse los datos de abajo manualmente.')}
            </Message>
        );
    }

    renderCampaigns() {
        const fieldForLabel = this.props.fields.find((field) => field.id === this.props.selectedFieldId);
        const fieldLabel = `${_('Lote')} ${fieldForLabel.name}, ${fieldForLabel.location}`;
        const campaignsOptions = this.props.campaignsOptions.slice(0, -1);

        return (
            <Fragment>
                <Container
                    classNameRoot={styles.rootContainerBox}
                    className={styles.wrapperContainer}
                    color='pink'
                    gradient='center-v2'
                >
                    <div className={styles.box}>
                        {isMobile() && this.state.collapsed && (
                            <div className={styles.collapsedBtn}>
                                <div className={styles.collapsedData}>
                                    <Title className={styles.fieldText}>{_('Lote')}</Title>
                                    <Title className={styles.selectedFieldLabel}>{fieldLabel}</Title>
                                </div>
                                <i
                                    onClick={() => this.handleCollapseClick()}
                                    className='material-icons'
                                    id={styles.expandedIcon}
                                >
                                    {this.state.collapsed ? 'expand_more' : 'expand_less'}
                                </i>
                            </div>
                        )}
                        {!this.state.collapsed && (
                            <div className={styles.wrapper}>
                                <div className={styles.fieldAndTempeture}>
                                    <div className={styles.fieldData}>
                                        <p className={styles.fieldText}>{_('Lote')}</p>
                                        <div className={styles.noCollapsedBtn}>
                                            <Select
                                                size='md'
                                                color={settings.app.theme}
                                                className={styles.fieldStyle}
                                                value={this.props.selectedFieldId}
                                                options={this.props.fields.map(({ name, location, id }) => ({
                                                    label: `${name} - ${location}`,
                                                    value: id,
                                                }))}
                                                onChange={(event) => this.handleZoneSelection(event)}
                                            />
                                        </div>
                                        <Button
                                            onClick={() => {
                                                if (settings.app.theme === 'expertobayer') {
                                                    this.setState({ showNewFieldModal: true });
                                                } else {
                                                    this.context.history(reverse('fields.NewField'));
                                                }
                                            }}
                                        >
                                            {_(' + Creá un lote')}
                                        </Button>
                                    </div>
                                    {!this.state.collapsed && (
                                        <div className={styles.fieldTempeture}>
                                            <p className={styles.fieldText}>{_('Temperatura promedio histórico')}</p>
                                            <Input
                                                extraClass={styles.tempStyle}
                                                type='number'
                                                onChange={(value) => this.handleTempChange(value)}
                                                size='md'
                                                color={settings.app.theme}
                                                value={this.state.temperature}
                                                error={
                                                    this.state.temperature === ''
                                                        ? _('Este campo es requerido.')
                                                        : false
                                                }
                                            />
                                        </div>
                                    )}
                                </div>
                                <div className={styles.select}>
                                    <div className={styles.calculatorOptions}>
                                        <div className={styles.selectComponent}>
                                            <Select
                                                value={this.props.campaignFrom}
                                                options={campaignsOptions}
                                                placeholder={_('Campaña')}
                                                title={_('Desde campaña')}
                                                onChange={(val) => this.campaignFromChanged(val)}
                                                color={settings.app.theme}
                                                titleStyles={styles.fieldText}
                                                error={this.state.campaignFromError ? true : false}
                                                errorMsg={_('Este campo es requerido.')}
                                                maxHeight='200px'
                                            />
                                        </div>
                                        <div className={styles.selectComponent}>
                                            <Select
                                                value={this.props.campaignTo}
                                                options={this.props.campaignsOptions
                                                    .filter((el) => el.value > this.props.campaignFrom)
                                                    .slice(0, 10)}
                                                placeholder={_('Campaña')}
                                                title={_('Hasta campaña')}
                                                onChange={(val) => this.campaignToChanged(val)}
                                                color={settings.app.theme}
                                                titleStyles={styles.fieldText}
                                                error={this.state.campaignToError ? true : false}
                                                errorMsg={_('Este campo es requerido.')}
                                                maxHeight='200px'
                                            />
                                        </div>
                                    </div>
                                    <div className={styles.calculatorOptionsTwo}>
                                        <div className={styles.selectComponent}>
                                            <Select
                                                value={this.props.farmingType}
                                                options={this.props.farmingOptions}
                                                placeholder={_('Labranza')}
                                                title={_('Tipo de labranza')}
                                                onChange={(val) => this.props.setFarmingType(val)}
                                                color={settings.app.theme}
                                                titleStyles={styles.fieldText}
                                            />
                                        </div>
                                        <div className={styles.selectComponent}>
                                            <Select
                                                value={this.props.soilType && this.props.soilType.label}
                                                options={this.props.allSoilTypes || []}
                                                placeholder={_('Suelo')}
                                                title={_('Tipo de suelo')}
                                                onChange={(v) => this.setSoilTypeValue(v, 'scroll')}
                                                color={settings.app.theme}
                                                titleStyles={styles.fieldText}
                                            />
                                            {this.state.otherSoilType && (
                                                <p className={styles.visible}>
                                                    {_(
                                                        'Eligiendo "Otros" en tipo de suelo, deberán cargarse los datos de abajo manualmente.'
                                                    )}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                {this.props.soilType && this.props.farmingType !== undefined && (
                                    <div className={styles.estimatedData}>
                                        <Title color='dark' size='md'>
                                            {_(
                                                'Los siguientes datos son estimados por su serie de suelos. Para mayor precisión complete con sus propios datos.'
                                            )}
                                            {' (*)'}
                                        </Title>
                                        <div>
                                            <div className={styles.input}>
                                                <div>
                                                    <Input
                                                        value={
                                                            this.props.clay === undefined
                                                                ? this.props.soilType.clay
                                                                : this.props.clay
                                                        }
                                                        type='number'
                                                        title={_('Arcilla en el suelo (%)')}
                                                        onChange={(v) => this.setClayValue(v)}
                                                        color={settings.app.theme}
                                                        extraClass={styles.widthInput}
                                                        titleStyles={styles.fieldText}
                                                        onKeyDown={(e) => this.handleOnKeyPress(e)}
                                                        error={
                                                            this.props.clay === 0 ||
                                                            this.props.clay === '' ||
                                                            this.props.clay === '0'
                                                                ? _('Este campo es requerido.')
                                                                : false
                                                        }
                                                    />
                                                </div>
                                                <div>
                                                    <Input
                                                        value={
                                                            this.props.organicMaterial === undefined
                                                                ? this.props.soilType.organicMaterial
                                                                : this.props.organicMaterial
                                                        }
                                                        type='number'
                                                        onKeyDown={(e) => this.handleOnKeyPress(e)}
                                                        title={_('% de Materia orgánica')}
                                                        onChange={(v) => this.setOrganicMaterialValue(v)}
                                                        color={settings.app.theme}
                                                        extraClass={styles.widthInput}
                                                        titleStyles={styles.fieldText}
                                                        error={
                                                            this.props.organicMaterial === 0 ||
                                                            this.props.organicMaterial === '' ||
                                                            this.props.organicMaterial === '0'
                                                                ? _('Este campo es requerido.')
                                                                : false
                                                        }
                                                    />
                                                </div>
                                                <div>
                                                    <Input
                                                        value={
                                                            this.props.density === undefined
                                                                ? this.props.soilType.apparentDensity
                                                                : this.props.density
                                                        }
                                                        type='number'
                                                        onKeyDown={(e) => this.handleOnKeyPress(e)}
                                                        title={_('Densidad aparente (gr/cm3)')}
                                                        onChange={(v) => this.setDensityValue(v)}
                                                        color={settings.app.theme}
                                                        extraClass={styles.widthInput}
                                                        titleStyles={styles.fieldText}
                                                        error={
                                                            this.props.density === 0 ||
                                                            this.props.density === '' ||
                                                            this.props.density === '0'
                                                                ? _('Este campo es requerido.')
                                                                : false
                                                        }
                                                    />
                                                </div>
                                            </div>
                                            <Title className={styles.disclaimer}>
                                                {'(*) '}
                                                {_('Estas mediciones corresponden a extracto de suelo de 0-20cm.')}
                                            </Title>
                                        </div>
                                        <div className={styles.loadBox}>
                                            <MuiButton variant='text' onClick={() => this.handleLoadMore('scroll')}>
                                                {_('cargá tus cultivos')}
                                            </MuiButton>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </Container>
            </Fragment>
        );
    }

    renderResults() {
        const { selectedCampaigns } = this.props;
        const data = this.props.calculationResult;
        if (!data) return null;

        const weightAreaUnit = this.context.units.getUnit('weight/area');
        const areaUnit = this.context.units.getUnit('area');

        return (
            <div className={styles.containerCampaignsCarbonresult}>
                <div className={styles.titleCampaignsCarbonresult}>
                    <Title className={styles.subtitle}>
                        {_(
                            'Cargá los cultivos de cada campaña y sus rendimientos. Podés cargar hasta 3 cultivos por campaña.'
                        )}
                    </Title>
                </div>
                <div className={styles.campaignsCarbonResult}>
                    <div className={styles.campaigns}>
                        {!isMobile() && <div className={styles.spaceImage} />}
                        <Title className={styles.titleOrganicCarbon}>{_('Campañas')}</Title>
                        {Object.keys(selectedCampaigns).map((season, seasonIndex) => {
                            return (
                                <Box
                                    sx={
                                        this.state.selectedSeason === seasonIndex && {
                                            border: (t) => `1px solid ${t.palette.primary.main}`,
                                        }
                                    }
                                >
                                    <div className={styles.campaign} key={season}>
                                        <div
                                            key={season}
                                            className={styles.dropdownHeader}
                                            onClick={(ev) => this.setSelectedSeason(seasonIndex)}
                                        >
                                            <div>{`${season}/${Number(season) + 1}`}</div>
                                            <Typography>+</Typography>
                                        </div>

                                        <div
                                            className={styles.dropdownContent}
                                            style={{
                                                display: this.state.selectedSeason !== seasonIndex ? 'none' : 'block',
                                            }}
                                        >
                                            {selectedCampaigns[season]['crops'].map((crop, cropIndex) => {
                                                return (
                                                    <div className={styles.campaignCrop} key={cropIndex}>
                                                        <Select
                                                            value={crop['key']}
                                                            key={cropIndex}
                                                            options={crop.options}
                                                            placeholder={_('Cultivo')}
                                                            onChange={(_, value) =>
                                                                this.handleCropChange('crop', cropIndex, season, value)
                                                            }
                                                            className={styles.selectDropdown}
                                                            color={settings.app.theme}
                                                            maxHeight='200px'
                                                        />
                                                        <Input
                                                            type='number'
                                                            suffix={`(${weightAreaUnit})`}
                                                            placeholder={_('Rendimiento')}
                                                            onChange={(value) =>
                                                                this.handleCropChange(
                                                                    'yield',
                                                                    cropIndex,
                                                                    season,
                                                                    this.context.units.fromWeightPerArea(value) / 1000
                                                                )
                                                            }
                                                            color={settings.app.theme}
                                                            extraClass={styles.yield}
                                                            onKeyDown={(e) => {
                                                                if (
                                                                    e.key === 'Enter' &&
                                                                    seasonIndex + 1 !==
                                                                        Object.keys(selectedCampaigns).length
                                                                ) {
                                                                    this.setState((prevState) => ({
                                                                        selectedSeason: prevState.selectedSeason + 1,
                                                                    }));
                                                                }
                                                            }}
                                                        />
                                                        {selectedCampaigns[season]['crops'].length > 1 && (
                                                            <button
                                                                className={styles.removeCrop}
                                                                onClick={(v) =>
                                                                    this.removeCropFromCampaign(season, cropIndex)
                                                                }
                                                            >
                                                                x
                                                            </button>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                            <MuiButton type='text' onClick={() => this.addCropToCampaign(season)}>
                                                {_('+ Agregar')}
                                            </MuiButton>
                                        </div>
                                    </div>
                                </Box>
                            );
                        })}
                    </div>
                    <div className={styles.carbonResult}>
                        <div>
                            <div className={styles.tooltip}>
                                <Tooltip
                                    largeText
                                    text={_(
                                        'Indicador de calidad del suelo altamente relacionado con la Materia Orgánica. Simula las reservas o stock de C en el suelo.'
                                    )}
                                >
                                    <div className={styles.SIMInfo}>
                                        <span>
                                            {_('Carbono orgánico en el suelo (SIM - Mg/{{unit}}-1)', {
                                                unit: areaUnit,
                                            })}
                                        </span>
                                        <i id={styles.icon} className={'material-icons'}>
                                            info
                                        </i>
                                    </div>
                                </Tooltip>
                                <Tooltip
                                    largeText
                                    text={_(
                                        'CO2 secuestrado por las plantas, resultado de las diferencias entre el CO2 atmosférico absorbido durante el proceso de la fotosíntesis y el CO2 emitido durante la respiración, interaccionando simultáneamente con la composición del suelo.  1 tn de C Suelo equivale a 3,663 TN de CO2'
                                    )}
                                >
                                    <div className={styles.CO2Info}>
                                        <span>{_('Balance CO2 por campaña (Mg/{{unit}}-1)', { unit: areaUnit })}</span>
                                        <i id={styles.secondIcon} className={'material-icons'}>
                                            info
                                        </i>
                                    </div>
                                </Tooltip>
                            </div>
                            <div className={styles.intialCarbonText}>
                                {data.length === 0 ? (
                                    <div className={styles.initialCarbonNumber}> 00,00 </div>
                                ) : (
                                    <div className={styles.initialCarbonNumber}> </div>
                                )}
                                <div className={styles.initialCarbonResult}> {_('Carbono Inicial')} </div>
                            </div>
                            {data.length === 0 && <div className={styles.space} />}
                            {data.length > 0 && (
                                <div className={styles.carbonResultContainer}>
                                    <div className={styles.organicCarbon}>
                                        {data.map((season) => {
                                            return (
                                                <div className={styles.initial} key={season.season}>
                                                    {this.round(season.co.simulated, 2)}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div className={styles.initialCarbonContainer}>
                                        {data.map((season) => {
                                            return (
                                                <Fragment>
                                                    <div className={styles.initial} key={season.season}>
                                                        {season.co.fixed === 0 ? (
                                                            <br />
                                                        ) : season.co.fixed > 0 ? (
                                                            `+${this.round(season.co.fixed, 3)}`
                                                        ) : (
                                                            this.round(season.co.fixed, 3)
                                                        )}
                                                    </div>
                                                </Fragment>
                                            );
                                        })}
                                    </div>
                                    <div className={styles.co2Div}>
                                        {data.map((season) => {
                                            return (
                                                <div key={season.season} className={styles.co2}>
                                                    {_('suelo')}
                                                    {season.co.fixed > 0 ? (
                                                        <div className={styles.green} />
                                                    ) : (
                                                        <div className={styles.red} />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className={styles.graphicsBox}>
                            <MuiButton variant='text' onClick={() => this.handleShowGraphics('scroll')}>
                                {_('Ver gráficos')}
                            </MuiButton>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    renderLineGraphic() {
        let data = [];
        let maxMinValues = [];
        let max = '';
        let min = '';

        if (this.props.calculationResult.length > 1) {
            data = this.props.calculationResult.map((res) => {
                const graphicData = {
                    campaign: res.season,
                    SIM: this.round(res.co.simulated, 2),
                };
                return graphicData;
            });
            maxMinValues = this.getMaxAndMinValues(this.props.calculationResult);
            max = Math.max(...maxMinValues);
            max = max * (1 + 0.2);
            max = this.round(max, 2);
            min = Math.min(...maxMinValues);
            min *= 1 - 0.2;
            min = this.round(min, 2);
        }

        return (
            <div className={styles.graphicWrapper}>
                <Title theme={settings.app.theme} size='lg' className={styles.graphicsTitle}>
                    {_('Carbono orgánico en el suelo (SIM)')}
                </Title>
                <LineChart
                    width={isPhone() ? 300 : 430}
                    height={isPhone() ? 200 : 300}
                    data={data}
                    margin={{
                        top: 30,
                        bottom: 0,
                        right: 5,
                    }}
                >
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis dataKey='campaign' />
                    <YAxis type='number' domain={[min, max]}>
                        <Label value='SIM' offset={15} position='top' />
                    </YAxis>
                    <TooltipGraphics />
                    <Line type='monotone' dataKey='SIM' stroke='#A46C6F' activeDot={{ r: 8 }} />
                </LineChart>
            </div>
        );
    }

    renderBarGraphic() {
        let data = [];
        if (this.props.calculationResult.length > 1) {
            data = this.props.calculationResult;
            data = data.map((res) => {
                const graphicData = {
                    campaign: res.season,
                    CO2: this.round(res.co.fixed, 2),
                };
                return graphicData;
            });
            data.shift();
        }
        const CO2 = data.map((campaigns) => {
            return campaigns.CO2;
        });

        return (
            <div className={styles.graphicWrapper}>
                <Title theme={settings.app.theme} size='lg' className={styles.graphicsTitle}>
                    {_('CO2 fijado')}
                </Title>
                <BarChart
                    width={isPhone() ? 300 : 430}
                    height={isPhone() ? 200 : 300}
                    data={data}
                    margin={{
                        top: 30,
                        bottom: 0,
                    }}
                    maxBarSize={8}
                >
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis dataKey='campaign' />
                    <YAxis type='number' domain={[-5, 5]}>
                        <Label value='CO2' offset={15} position='top' />
                    </YAxis>
                    <Tooltip />
                    <ReferenceLine y={0} stroke='#000' />
                    <Bar dataKey='CO2' barSize={50} label={{ fill: 'white', fontSize: 'auto' }}>
                        {CO2.map((c) => (
                            <Cell key={c} fill={c > 0 ? '#30976B' : '#DB5F65'} />
                        ))}
                    </Bar>
                </BarChart>
            </div>
        );
    }

    render() {
        let lastResult = {};
        if (this.props.calculationResult.length !== 0) {
            lastResult = this.props.calculationResult[this.props.calculationResult.length - 1];
        }

        return (
            <Fragment>
                <Container
                    classNameRoot={classNames(styles.rootContainer, styles.rootContainerHeader)}
                    className={styles.wrapperContainer}
                >
                    {this.state.errorMessage && isMobile() && this.renderErrorMessage()}
                    <HeaderCarbonCalculator
                        descriptionText={_(
                            'Cargá la franja de campañas que quieras. Podés elegir campañas anteriores o futuras.'
                        )}
                        textAlign='AR'
                        title={_('Simulador de rotaciones sustentables')}
                        firstParagraph={_('Evalua la dinámica del suelo en tus rotaciones.')}
                        secondParagraph={_('Para ver más información, poder acceder a la ')}
                    />
                </Container>
                {this.renderCampaigns()}
                <Container className={styles.wrapperContainer}>
                    {this.state.showCampaigns &&
                        this.props.campaignFrom !== undefined &&
                        this.props.campaignTo !== undefined &&
                        this.renderResults()}
                    {isMobile() &&
                        this.props.calculationResult.length !== 0 &&
                        lastResult !== undefined &&
                        this.state.showSnackbar && (
                            <Snackbar
                                onClick={() => this.setState({ showSnackbar: false })}
                                children={
                                    <div className={styles.snackBar}>
                                        <div className={styles.titleWrapper}>
                                            <Title className={styles.snackBarText}>
                                                {_('Carbono orgánico en el suelo (SIM)')}
                                            </Title>
                                            <Title className={styles.snackBarTitle}>
                                                {this.round(lastResult.co.simulated, 2)}
                                            </Title>
                                        </div>
                                        <div className={styles.titleWrapper}>
                                            <Title className={styles.snackBarText}>{_('CO2 Fijado')}</Title>
                                            <Title className={styles.snackBarTitle}>
                                                {this.round(lastResult.co.fixed, 3)}
                                            </Title>
                                        </div>
                                        {lastResult.co.fixed > 0 ? (
                                            <div className={styles.greenSnackBar} />
                                        ) : (
                                            <div className={styles.redSnackBar} />
                                        )}
                                    </div>
                                }
                                showSnackbar={this.state.showSnackbar}
                            />
                        )}
                    {this.state.showGraphics && (
                        <div className={isPhone() ? styles.graphicsForPhone : styles.graphics}>
                            {this.renderLineGraphic()}
                            {this.renderBarGraphic()}
                        </div>
                    )}
                    <div id='scroll' style={{ float: 'left', clear: 'both' }} />
                    <div className={styles.containerDisclaimer}>
                        <div className={styles.intaContainer}>
                            <Title className={styles.disclaimer}>
                                {_(
                                    'Esta herramienta es un desarrollo conjunto entre el Instituto Nacional de Tecnología Agropecuaria de Argentina y Bayer Crop Science.'
                                )}
                            </Title>
                            <div className={styles.inta} />
                        </div>

                        {this.state.crop === 'corn' && (
                            <FooterCarbonCalculator
                                selectedField={this.props.fields.find(
                                    (field) => field.id === this.props.selectedFieldId
                                )}
                                userNodeId={this.props.userNodeId}
                            />
                        )}
                    </div>
                </Container>
                <NewFieldGrowersModal
                    open={this.state.showNewFieldModal}
                    setOpen={() => this.setState({ showNewFieldModal: false })}
                    growerUserNodeId={this.props.userNodeId}
                    trackEntrySource='carbon-calculator'
                />
            </Fragment>
        );
    }
}

const CarbonCalculatorWrapper = (props: any) => {
    const [selectedFieldId, setSelectedFieldId] = useState(props.fieldId);
    const country = useSelector(getSessionCountry);

    const fieldQuery = useFieldById({ id: selectedFieldId });
    const userNodeId = fieldQuery.data?.userNode;
    const fieldsQuery = useMyFields(
        {
            page: 1,
            pageSize: 10000,
            country: country.code,
            selectOnly: 'id,lat,lng,name,location',
            userNodeId,
        },
        {
            enabled: !!userNodeId,
        }
    );

    if (!fieldsQuery.data?.results || fieldsQuery.isValidating || fieldQuery.isValidating)
        return (
            <Stack justifyContent='center' alignItems='center' height={'100%'}>
                <Spinner />
            </Stack>
        );

    const fieldZoneIds = fieldQuery.data?.node?.zones.map(({ id }) => id).join('|');

    return (
        <CarbonCalculator
            {...props}
            fields={fieldsQuery.data.results}
            fieldZoneIds={fieldZoneIds}
            selectedFieldId={selectedFieldId}
            setSelectedFieldId={setSelectedFieldId}
            userNodeId={userNodeId}
        />
    );
};
export default CarbonCalculatorWrapper;
